/**
 * Chrome collision detection (ruling 2026-08-13): the backing tint
 * exists ONLY where chrome actually collides with another object.
 * Per band — a note whose head band crosses a neighbor gets
 * `data-coc-collide-head` on its wrapper (same for the foot) and the
 * CSS keys the tint off those attributes. No collision → no attribute
 * → no chip, in every state.
 *
 * Imperative by design: rects are measured in one rAF pass, throttled
 * behind a 120ms trailing timer, and results land as DOM attributes —
 * no React state fan-out, nothing on the drag/zoom hot paths beyond
 * the throttled sweep (O(notes × nodes), a few hundred rect reads).
 */

const colliders = new Map<string, HTMLElement>();
let raf = 0;
let settle: ReturnType<typeof setTimeout> | null = null;

export function registerChromeCollider(id: string, el: HTMLElement | null) {
  if (el) colliders.set(id, el);
  else colliders.delete(id);
  scheduleChromeCollision();
}

/** Throttled recompute — call on any node/viewport change. */
export function scheduleChromeCollision() {
  if (settle) return;
  settle = setTimeout(() => {
    settle = null;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(recompute);
  }, 120);
}

type Entry = {
  el: HTMLElement;
  node: Element | null;
  head: DOMRect | null;
  foot: DOMRect | null;
};

function bandRect(el: HTMLElement, selector: string): DOMRect | null {
  const band = el.querySelector<HTMLElement>(selector);
  if (!band) return null;
  const r = band.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? r : null;
}

function hits(a: DOMRect, b: DOMRect): boolean {
  return (
    a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom
  );
}

function recompute() {
  if (colliders.size === 0) return;
  // Sections COUNT as collision targets, deliberately (ruled
  // 2026-08-13): their fills can be opaque, so chrome crossing them
  // needs the chip like over any other object. The consequence — notes
  // nested in a section carry a subtle chip at all times — is the
  // accepted price of one consistent rule; a transparency-conditional
  // exclusion was considered and rejected (identical layouts would
  // tint differently).
  const nodes = [...document.querySelectorAll<HTMLElement>(".react-flow__node")]
    .map((n) => ({ n, r: n.getBoundingClientRect() }))
    .filter(({ r }) => r.width > 0);

  const entries: Entry[] = [...colliders.values()].map((el) => ({
    el,
    node: el.closest(".react-flow__node"),
    head: bandRect(el, ".coc-head"),
    foot: bandRect(el, ".coc-foot"),
  }));

  for (const e of entries) {
    let head = false;
    let foot = false;
    for (const { n, r } of nodes) {
      if (n === e.node) continue;
      if (!head && e.head && hits(e.head, r)) head = true;
      if (!foot && e.foot && hits(e.foot, r)) foot = true;
      if (head && foot) break;
    }
    // Chrome-vs-chrome counts too: two titles stacked at low zoom
    // collide without either body overlapping.
    if (!head || !foot) {
      for (const o of entries) {
        if (o === e) continue;
        for (const band of [o.head, o.foot]) {
          if (!band) continue;
          if (!head && e.head && hits(e.head, band)) head = true;
          if (!foot && e.foot && hits(e.foot, band)) foot = true;
        }
        if (head && foot) break;
      }
    }
    e.el.toggleAttribute("data-coc-collide-head", head);
    e.el.toggleAttribute("data-coc-collide-foot", foot);
  }
}
